import React, { useState } from 'react';
import {
    Box,
    Button,
    Menu,
    MenuItem,
    Typography,
    Paper,
    Popover,
    IconButton,
    useTheme
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { Calendar, ChevronDown } from 'lucide-react';

dayjs.extend(isBetween);

const today = dayjs();
const currentQuarter = Math.floor((today.month()) / 3) + 1;
const getQuarterStart = (qtr, year) => dayjs(`${year}-${(qtr - 1) * 3 + 1}-01`).startOf('month');
const getQuarterEnd = (qtr, year) => getQuarterStart(qtr, year).add(2, 'month').endOf('month');

const presets = [
    { label: 'Today', range: [today, today] },
    { label: 'Yesterday', range: [today.subtract(1, 'day'), today.subtract(1, 'day')] },
    { label: 'Last 7 Days', range: [today.subtract(6, 'day'), today] },
    { label: 'This Week', range: [today.startOf('week'), today.endOf('week')] },
    { label: 'Last Week', range: [today.subtract(1, 'week').startOf('week'), today.subtract(1, 'week').endOf('week')] },
    { label: 'This Month', range: [today.startOf('month'), today.endOf('month')] },
    {
        label: 'Last Month', range: [
            today.subtract(1, 'month').startOf('month'),
            today.subtract(1, 'month').endOf('month')
        ]
    },
    {
        label: 'This Quarter', range: [
            getQuarterStart(currentQuarter, today.year()),
            getQuarterEnd(currentQuarter, today.year())
        ]
    },
    {
        label: 'Last Quarter', range: [
            getQuarterStart(currentQuarter - 1 === 0 ? 4 : currentQuarter - 1, currentQuarter - 1 === 0 ? today.year() - 1 : today.year()),
            getQuarterEnd(currentQuarter - 1 === 0 ? 4 : currentQuarter - 1, currentQuarter - 1 === 0 ? today.year() - 1 : today.year())
        ]
    },
    {
        label: 'Last Financial Year', range: [
            dayjs(`${today.year() - 1}-07-01`),
            dayjs(`${today.year()}-06-30`),
        ]
    },
];

const renderCustomDay = (day, _selectedDates, pickersDayProps, start, end, calendarType) => {
    const isStart = day.isSame(start, 'day');
    const isEnd = day.isSame(end, 'day');
    const isInRange = start && end && day.isAfter(start, 'day') && day.isBefore(end, 'day');

    let styles = {
        transition: 'all 0.3s ease'
    };

    if (calendarType === 'from') {
        if (isStart) {
            styles = {
                backgroundColor: '#0000CD',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '50%'
            };
        } else if (isInRange || isEnd) {
            styles = {
                border: '2px solid #64b5f6',
                color: '#fff',
                borderRadius: '999px'
            };
        }
    } else if (calendarType === 'to') {
        if (isEnd) {
            styles = {
                backgroundColor: '#873260',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '50%'
            };
        } else if (isInRange || isStart) {
            styles = {
                border: '2px solid #64b5f6',
                color: '#fff',
                borderRadius: '999px'
            };
        }
    }

    return <PickersDay {...pickersDayProps} selected={false} sx={styles} />;
};

const CompactDateRangePicker = ({ onChange, startDate, endDate }) => {
    const theme = useTheme();
    const [start, setStart] = useState(dayjs(startDate));
    const [end, setEnd] = useState(dayjs(endDate));
    const [anchorEl, setAnchorEl] = useState(null);
    const [presetAnchorEl, setPresetAnchorEl] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [activeCalendar, setActiveCalendar] = useState('from');

    const handleRangeChange = (newStart, newEnd) => {
        const s = newStart.format('YYYY-MM-DD');
        const e = newEnd.format('YYYY-MM-DD');
        if (s !== start.format('YYYY-MM-DD') || e !== end.format('YYYY-MM-DD')) {
            setStart(newStart);
            setEnd(newEnd);
            if (onChange) onChange([newStart, newEnd]);
        }
        setPresetAnchorEl(null);
    };

    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePresetClick = (event) => {
        setPresetAnchorEl(event.currentTarget);
    };

    const handlePresetClose = () => {
        setPresetAnchorEl(null);
    };

    const handleCalendarOpen = (calType) => {
        setActiveCalendar(calType);
        setCalendarOpen(true);
    };

    const handleCalendarClose = () => {
        setCalendarOpen(false);
    };

    const open = Boolean(anchorEl);
    const presetOpen = Boolean(presetAnchorEl);
    const id = open ? 'date-range-popover' : undefined;
    const presetId = presetOpen ? 'preset-menu' : undefined;

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Button
                variant="outlined"
                size="small"
                onClick={handleButtonClick}
                startIcon={<Calendar size={16} />}
                endIcon={<ChevronDown size={14} />}
                sx={{
                    bgcolor: '#333',
                    color: '#fff',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    height: '40px',
                    textTransform: 'none',
                    '&:hover': {
                        bgcolor: '#444',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                    }
                }}
            >
                {start.format('MMM DD')} - {end.format('MMM DD, YYYY')}
            </Button>

            <Menu
                id={id}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        width: 280,
                        p: 1,
                        bgcolor: '#1e1e1e',
                        color: '#fff'
                    }
                }}
            >
                <Box sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Button
                            size="small"
                            onClick={() => handleCalendarOpen('from')}
                            sx={{
                                mr: 1,
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                bgcolor: '#333',
                                color: '#fff',
                                '&:hover': {
                                    bgcolor: '#444',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                }
                            }}
                        >
                            From: {start.format('MMM DD, YYYY')}
                        </Button>

                        <Button
                            size="small"
                            onClick={() => handleCalendarOpen('to')}
                            sx={{
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                bgcolor: '#333',
                                color: '#fff',
                                '&:hover': {
                                    bgcolor: '#444',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                }
                            }}
                        >
                            To: {end.format('MMM DD, YYYY')}
                        </Button>
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={handlePresetClick}
                        endIcon={<ChevronDown size={14} />}
                        sx={{
                            mb: 1,
                            mt: 1,
                            textTransform: 'none',
                            borderColor: 'rgba(255, 255, 255, 0.2)',
                            bgcolor: '#333',
                            color: '#fff',
                            '&:hover': {
                                bgcolor: '#444',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                            }
                        }}
                    >
                        Select Range Preset
                    </Button>

                    <Menu
                        id={presetId}
                        anchorEl={presetAnchorEl}
                        open={presetOpen}
                        onClose={handlePresetClose}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                width: 200,
                                maxHeight: 300,
                                bgcolor: '#1e1e1e',
                                color: '#fff',
                                '& .MuiMenuItem-root': {
                                    '&:hover': {
                                        bgcolor: '#333'
                                    }
                                }
                            }
                        }}
                    >
                        {presets.map((preset) => (
                            <MenuItem
                                key={preset.label}
                                onClick={() => handleRangeChange(preset.range[0], preset.range[1])}
                                dense
                            >
                                {preset.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Menu>

            <Popover
                open={calendarOpen}
                onClose={handleCalendarClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        p: 2,
                        bgcolor: '#1e1e1e',
                        color: '#fff',
                        '& .MuiTypography-root': {
                            color: '#fff !important'
                        },
                        '& .MuiPickersDay-root': {
                            color: '#fff !important',
                            backgroundColor: 'transparent !important'
                        },
                        '& .MuiPickersDay-root:not(.Mui-selected)': {
                            backgroundColor: 'transparent !important'
                        },
                        '& .MuiPickersDay-root:hover': {
                            backgroundColor: '#333 !important'
                        },
                        '& .MuiPickersCalendarHeader-root': {
                            color: '#fff !important',
                            backgroundColor: 'transparent !important'
                        },
                        '& .MuiPickersArrowSwitcher-root .MuiIconButton-root': {
                            color: '#fff !important'
                        },
                        '& .MuiPickersYear-yearButton': {
                            color: '#fff !important',
                            backgroundColor: 'transparent !important',
                            '&:hover': {
                                backgroundColor: '#333 !important'
                            }
                        },
                        '& .MuiPickersMonth-monthButton': {
                            color: '#fff !important',
                            backgroundColor: 'transparent !important',
                            '&:hover': {
                                backgroundColor: '#333 !important'
                            }
                        },
                        '& .MuiDateCalendar-root': {
                            backgroundColor: '#1e1e1e !important',
                            color: '#fff !important'
                        },
                        '& .MuiPickersCalendarHeader-label': {
                            color: '#fff !important'
                        },
                        '& .MuiPickersCalendarHeader-switchViewButton': {
                            color: '#fff !important'
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                            color: '#fff !important'
                        },
                        '& .MuiPickersSlideTransition-root': {
                            backgroundColor: '#1e1e1e !important'
                        },
                        '& .MuiYearCalendar-root': {
                            backgroundColor: '#1e1e1e !important'
                        },
                        '& .MuiMonthCalendar-root': {
                            backgroundColor: '#1e1e1e !important'
                        },
                        '& .MuiPaper-root': {
                            backgroundColor: '#1e1e1e !important'
                        }
                    }
                }}
            >
                <Box sx={{ width: 300 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {activeCalendar === 'from' ? 'Select Start Date' : 'Select End Date'}
                    </Typography>

                    <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={activeCalendar === 'from' ? start : end}
                        onChange={(newValue) => {
                            if (activeCalendar === 'from') {
                                const newEnd = newValue.isAfter(end) ? newValue : end;
                                handleRangeChange(newValue, newEnd);
                            } else {
                                const newStart = newValue.isBefore(start) ? newValue : start;
                                handleRangeChange(newStart, newValue);
                            }
                            handleCalendarClose();
                        }}
                        slots={{
                            day: (props) => renderCustomDay(
                                props.day,
                                props.selectedDays,
                                props,
                                start,
                                end,
                                activeCalendar
                            )
                        }}
                        sx={{
                            backgroundColor: '#1e1e1e !important',
                            color: '#fff !important',
                            '& .MuiDateCalendar-root': {
                                backgroundColor: '#1e1e1e !important'
                            },
                            '& .MuiPickersCalendarHeader-root': {
                                backgroundColor: '#1e1e1e !important'
                            }
                        }}
                    />
                </Box>
            </Popover>
        </LocalizationProvider>
    );
};

export default CompactDateRangePicker;
